import React from 'react';

import {
  configureAnalytics,
  createAnalyticsClient,
  createFirebaseAnalyticsTransport,
  startSentryTracer,
} from '@ragu-components/ord-analytics';
import ReactDOM from 'react-dom/client';

import { version } from '../package.json';

import Ingord from './Ingord';
import { analyticsLogger } from './lib/logger';

const envOpt = (value: unknown): string | undefined => (typeof value === 'string' && value !== '' ? value : undefined);

const envBool = (value: unknown): boolean | undefined => {
  if (typeof value !== 'string' || value === '') return undefined;
  return value === 'true' || value === '1';
};

const firebaseCommonConfig = {
  apiKey: envOpt(import.meta.env.REACT_APP_FIREBASE_API_KEY),
  authDomain: envOpt(import.meta.env.REACT_APP_FIREBASE_AUTH_DOMAIN),
  projectId: envOpt(import.meta.env.REACT_APP_FIREBASE_PROJECT_ID),
};

const firebaseDevTarget = {
  appId: envOpt(import.meta.env.REACT_APP_FIREBASE_APP_ID_DEV),
  measurementId: envOpt(import.meta.env.REACT_APP_FIREBASE_MEASUREMENT_ID_DEV),
};

const firebaseProdTarget = {
  appId: envOpt(import.meta.env.REACT_APP_FIREBASE_APP_ID_PROD),
  measurementId: envOpt(import.meta.env.REACT_APP_FIREBASE_MEASUREMENT_ID_PROD),
};

const hasFirebaseTarget = (target: {
  appId?: string;
  measurementId?: string;
}): target is {
  appId: string;
  measurementId: string;
} => target.appId !== undefined && target.measurementId !== undefined;

const explicitFirebaseDebug = envBool(import.meta.env.REACT_APP_FIREBASE_DEBUG);
const hasFirebaseDevTarget = hasFirebaseTarget(firebaseDevTarget);
const hasFirebaseProdTarget = hasFirebaseTarget(firebaseProdTarget);
const useFirebaseDevTarget =
  ((explicitFirebaseDebug === true || import.meta.env.DEV) && hasFirebaseDevTarget) ||
  (!hasFirebaseProdTarget && hasFirebaseDevTarget);
const firebaseTarget = useFirebaseDevTarget ? firebaseDevTarget : firebaseProdTarget;
const firebaseConfig =
  firebaseCommonConfig.apiKey !== undefined &&
  firebaseCommonConfig.authDomain !== undefined &&
  firebaseCommonConfig.projectId !== undefined &&
  hasFirebaseTarget(firebaseTarget)
    ? {
        apiKey: firebaseCommonConfig.apiKey,
        authDomain: firebaseCommonConfig.authDomain,
        projectId: firebaseCommonConfig.projectId,
        appId: firebaseTarget.appId,
        measurementId: firebaseTarget.measurementId,
      }
    : null;

const analyticsDebug = explicitFirebaseDebug ?? (import.meta.env.DEV || useFirebaseDevTarget);

if (analyticsDebug) {
  analyticsLogger.setLevel('debug');
  analyticsLogger.debug('[analytics] debug mode enabled');
}

const analyticsClient = createAnalyticsClient({
  debug: analyticsDebug,
  onError: (error, context) => {
    analyticsLogger.error('Error logging analytics event:', context, error);
  },
});

analyticsClient.registerTransport(
  createFirebaseAnalyticsTransport({
    appName: 'ingord',
    config: firebaseConfig,
    debug: analyticsDebug,
    logger: analyticsLogger,
    runtime: 'web',
  })
);

configureAnalytics(analyticsClient);

const sentryDsn =
  import.meta.env.DEV || envOpt(import.meta.env.REACT_APP_SENTRY_DSN_PROD) === undefined
    ? envOpt(import.meta.env.REACT_APP_SENTRY_DSN_DEV)
    : envOpt(import.meta.env.REACT_APP_SENTRY_DSN_PROD);
const sentryEnvironment = import.meta.env.DEV || useFirebaseDevTarget ? 'development' : 'production';

startSentryTracer({
  dsn: sentryDsn,
  release: `ingord@${version}`,
  environment: sentryEnvironment,
  runtime: 'web',
});

const root = ReactDOM.createRoot(document.getElementById('root') as unknown as HTMLElement);
root.render(
  <React.StrictMode>
    <Ingord />
  </React.StrictMode>
);
